export type VatIdValidationError = 'required' | 'invalid_format' | 'invalid'

interface VatIdConfig {
  prefix: string
  placeholder: string
  rawMaxLength: number
  inputMaxLength: number
  pattern: RegExp
}

export const DEFAULT_COUNTRY_CODE = 'PL'
const GENERIC_VAT_ID_MAX_LENGTH = 20

export const VAT_ID_CONFIGS: Record<string, VatIdConfig> = {
  AT: { prefix: 'AT', placeholder: 'U12345678', rawMaxLength: 9, inputMaxLength: 9, pattern: /^U\d{8}$/ },
  BE: { prefix: 'BE', placeholder: '0123456789', rawMaxLength: 10, inputMaxLength: 10, pattern: /^\d{10}$/ },
  BG: { prefix: 'BG', placeholder: '123456789', rawMaxLength: 10, inputMaxLength: 10, pattern: /^\d{9,10}$/ },
  CY: { prefix: 'CY', placeholder: '12345678A', rawMaxLength: 9, inputMaxLength: 9, pattern: /^\d{8}[A-Z]$/ },
  CZ: { prefix: 'CZ', placeholder: '12345678', rawMaxLength: 10, inputMaxLength: 10, pattern: /^\d{8,10}$/ },
  DE: { prefix: 'DE', placeholder: '123456789', rawMaxLength: 9, inputMaxLength: 9, pattern: /^\d{9}$/ },
  DK: { prefix: 'DK', placeholder: '12345678', rawMaxLength: 8, inputMaxLength: 8, pattern: /^\d{8}$/ },
  EE: { prefix: 'EE', placeholder: '123456789', rawMaxLength: 9, inputMaxLength: 9, pattern: /^\d{9}$/ },
  ES: { prefix: 'ES', placeholder: 'A1234567B', rawMaxLength: 9, inputMaxLength: 9, pattern: /^[A-Z0-9]\d{7}[A-Z0-9]$/ },
  FI: { prefix: 'FI', placeholder: '12345678', rawMaxLength: 8, inputMaxLength: 8, pattern: /^\d{8}$/ },
  FR: { prefix: 'FR', placeholder: 'AB123456789', rawMaxLength: 11, inputMaxLength: 11, pattern: /^[A-Z0-9]{2}\d{9}$/ },
  GR: { prefix: 'EL', placeholder: '123456789', rawMaxLength: 9, inputMaxLength: 9, pattern: /^\d{9}$/ },
  HR: { prefix: 'HR', placeholder: '12345678901', rawMaxLength: 11, inputMaxLength: 11, pattern: /^\d{11}$/ },
  HU: { prefix: 'HU', placeholder: '12345678', rawMaxLength: 8, inputMaxLength: 8, pattern: /^\d{8}$/ },
  IE: { prefix: 'IE', placeholder: '1234567AB', rawMaxLength: 9, inputMaxLength: 9, pattern: /^\d{7}[A-Z]{1,2}$/ },
  IT: { prefix: 'IT', placeholder: '12345678901', rawMaxLength: 11, inputMaxLength: 11, pattern: /^\d{11}$/ },
  LT: { prefix: 'LT', placeholder: '123456789', rawMaxLength: 12, inputMaxLength: 12, pattern: /^(\d{9}|\d{12})$/ },
  LU: { prefix: 'LU', placeholder: '12345678', rawMaxLength: 8, inputMaxLength: 8, pattern: /^\d{8}$/ },
  LV: { prefix: 'LV', placeholder: '12345678901', rawMaxLength: 11, inputMaxLength: 11, pattern: /^\d{11}$/ },
  MT: { prefix: 'MT', placeholder: '12345678', rawMaxLength: 8, inputMaxLength: 8, pattern: /^\d{8}$/ },
  NL: { prefix: 'NL', placeholder: '123456789B01', rawMaxLength: 12, inputMaxLength: 12, pattern: /^\d{9}B\d{2}$/ },
  PL: { prefix: 'PL', placeholder: '123-456-78-90', rawMaxLength: 10, inputMaxLength: 13, pattern: /^\d{10}$/ },
  PT: { prefix: 'PT', placeholder: '123456789', rawMaxLength: 9, inputMaxLength: 9, pattern: /^\d{9}$/ },
  RO: { prefix: 'RO', placeholder: '1234567890', rawMaxLength: 10, inputMaxLength: 10, pattern: /^\d{2,10}$/ },
  SE: { prefix: 'SE', placeholder: '123456789012', rawMaxLength: 12, inputMaxLength: 12, pattern: /^\d{12}$/ },
  SI: { prefix: 'SI', placeholder: '12345678', rawMaxLength: 8, inputMaxLength: 8, pattern: /^\d{8}$/ },
  SK: { prefix: 'SK', placeholder: '1234567890', rawMaxLength: 10, inputMaxLength: 10, pattern: /^\d{10}$/ }
}

export const VAT_ID_COUNTRY_CODES = Object.keys(VAT_ID_CONFIGS)

const normalizeCountryCode = (countryCode?: string) => (countryCode || DEFAULT_COUNTRY_CODE).toUpperCase()

const getGenericVatIdConfig = (countryCode?: string): VatIdConfig => ({
  prefix: normalizeCountryCode(countryCode),
  placeholder: '',
  rawMaxLength: GENERIC_VAT_ID_MAX_LENGTH,
  inputMaxLength: GENERIC_VAT_ID_MAX_LENGTH,
  pattern: /^[A-Z0-9]{1,20}$/
})

export const getVatIdConfig = (countryCode?: string): VatIdConfig => VAT_ID_CONFIGS[normalizeCountryCode(countryCode)] ?? getGenericVatIdConfig(countryCode)

export const getVatIdPrefix = (countryCode?: string) => getVatIdConfig(countryCode).prefix

export const getVatIdPlaceholder = (countryCode?: string) => getVatIdConfig(countryCode).placeholder

export const getVatIdInputMaxLength = (countryCode?: string) => getVatIdConfig(countryCode).inputMaxLength

export const getVatIdCountryCodeFromValue = (value?: string) => {
  const normalizedValue = (value ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '')
  return VAT_ID_COUNTRY_CODES.find(countryCode => {
    const { prefix } = getVatIdConfig(countryCode)
    return normalizedValue.startsWith(prefix) || (prefix !== countryCode && normalizedValue.startsWith(countryCode))
  }) ?? DEFAULT_COUNTRY_CODE
}

const stripVatIdPrefix = (value: string, countryCode?: string) => {
  const normalizedValue = value.toUpperCase().replace(/[^0-9A-Z]/g, '')
  const normalizedCountryCode = normalizeCountryCode(countryCode)
  const { prefix } = getVatIdConfig(countryCode)

  if (normalizedValue.startsWith(prefix)) {
    return normalizedValue.slice(prefix.length)
  }

  if (prefix !== normalizedCountryCode && normalizedValue.startsWith(normalizedCountryCode)) {
    return normalizedValue.slice(normalizedCountryCode.length)
  }

  return normalizedValue
}

const formatPolishVatId = (value: string) => {
  if (value.length <= 3) return value
  if (value.length <= 6) return `${value.slice(0, 3)}-${value.slice(3)}`
  if (value.length <= 8) return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`

  return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 8)}-${value.slice(8)}`
}

export const normalizeVatId = (value: string, countryCode?: string) => {
  const { rawMaxLength } = getVatIdConfig(countryCode)
  const normalizedCountryCode = normalizeCountryCode(countryCode)
  const withoutPrefix = stripVatIdPrefix(value, normalizedCountryCode)

  if (normalizedCountryCode === 'PL') {
    return withoutPrefix.replace(/\D/g, '').slice(0, rawMaxLength)
  }

  return withoutPrefix.replace(/[^0-9A-Z]/g, '').slice(0, rawMaxLength)
}

export const formatVatIdInput = (value: string, countryCode?: string) => {
  const normalizedValue = normalizeVatId(value, countryCode)

  if (normalizeCountryCode(countryCode) === 'PL') {
    return formatPolishVatId(normalizedValue)
  }

  return normalizedValue
}

export const getVatIdInputValue = (value: string, countryCode?: string) => formatVatIdInput(value, countryCode)

export const buildVatIdWithPrefix = (value: string, countryCode?: string) => {
  const normalizedValue = normalizeVatId(value, countryCode)

  if (!normalizedValue) return ''

  return `${getVatIdPrefix(countryCode)}${normalizedValue}`
}

const isValidPolishChecksum = (value: string) => {
  if (/^(\d)\1{9}$/.test(value)) return false

  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
  const digits = value.split('').map(Number)
  const sum = weights.reduce((acc, weight, index) => acc + weight * digits[index], 0)

  return sum % 11 === digits[9]
}

export const validateVatId = (
  value: string,
  countryCode?: string
): {
  error: VatIdValidationError | null
  normalized: string
  prefixed: string
  isValid: boolean
} => {
  const normalizedValue = normalizeVatId(value, countryCode)
  const normalizedCountryCode = normalizeCountryCode(countryCode)
  const { pattern } = getVatIdConfig(countryCode)

  if (!normalizedValue) {
    return { error: 'required', normalized: normalizedValue, prefixed: '', isValid: false }
  }

  if (!pattern.test(normalizedValue)) {
    return {
      error: 'invalid_format',
      normalized: normalizedValue,
      prefixed: buildVatIdWithPrefix(normalizedValue, countryCode),
      isValid: false
    }
  }

  if (normalizedCountryCode === 'PL' && !isValidPolishChecksum(normalizedValue)) {
    return {
      error: 'invalid',
      normalized: normalizedValue,
      prefixed: buildVatIdWithPrefix(normalizedValue, countryCode),
      isValid: false
    }
  }

  return {
    error: null,
    normalized: normalizedValue,
    prefixed: buildVatIdWithPrefix(normalizedValue, countryCode),
    isValid: true
  }
}

export const getVatIdErrorTranslationKey = (error: VatIdValidationError | null) => {
  switch (error) {
    case 'required':
      return 'buyer_data.tax_id_required'
    case 'invalid_format':
      return 'buyer_data.tax_id_invalid_format'
    case 'invalid':
      return 'buyer_data.tax_id_invalid'
    default:
      return null
  }
}
